import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { LoadingDialogComponent } from "@cotecna/dialogs";

import { InspectionDetailsService } from "../../services/inspection-details.service";

export interface PicturesReportTemplate {
  id: string;
  name: string;
}

export interface GeneratePicturesReportDialogData {
  templates: PicturesReportTemplate[];
  inspectionId: string;
}

export interface GeneratePicturesReportDialogResult {
  mode: "template" | "blank" | "edit";
  template?: PicturesReportTemplate;
  templateId: string | number | null;
}

@Component({
  selector: "im-generate-pictures-report-dialog",
  templateUrl: "./generate-pictures-report-dialog.component.html",
  styleUrls: ["./generate-pictures-report-dialog.component.scss"],
  standalone: false,
})
export class GeneratePicturesReportDialogComponent implements OnInit {
  public selectedMode: "template" | "blank" = "template";
  public selectedTemplate: PicturesReportTemplate | null = null;
  public templates: PicturesReportTemplate[] = [];

  public constructor(
    private readonly dialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly dialogRef: MatDialogRef<GeneratePicturesReportDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public dialogData: GeneratePicturesReportDialogData,
    private readonly inspectionDetailsService: InspectionDetailsService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    void this.loadTemplates();
  }

  private async loadTemplates(): Promise<void> {
    const response =
      await this.inspectionDetailsService.getPhotoReportTemplates();
    this.templates = (response ?? []).map((template) => ({
      id: template.templateId,
      name: template.templateName,
    }));

    if (this.templates.length > 0) {
      this.selectedTemplate = this.templates[0];
    }
    this.changeDetectorRef.detectChanges();
  }

  public get canSelect(): boolean {
    return this.selectedMode === "blank" || this.selectedTemplate !== null;
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  public edit(): void {
    this.dialogRef.close({
      templateId: this.selectedTemplate?.id ?? null,
    } as GeneratePicturesReportDialogResult);
  }

  public create(): void {
    if (!this.canSelect) return;
    this.dialogRef.close({
      templateId: null,
    } as GeneratePicturesReportDialogResult);
  }

  public async generate(): Promise<void> {
    if (!this.canSelect) return;

    const loading = this.dialog.open(LoadingDialogComponent, {
      autoFocus: false,
      disableClose: true,
      data: "Generating report...",
    });
    try {
      await this.inspectionDetailsService.generatePhotoReport(
        this.selectedTemplate?.id ?? "",
        this.dialogData.inspectionId,
      );
      this.dialogRef.close();
      this.snackBar.open(
        "Pictures Report is being generated and will be available in the Documents tab once ready.",
        "",
        { duration: 6000, panelClass: ["snack-success-green"] },
      );
    } finally {
      loading.close();
    }
  }
}
